import { useState, type FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/db4nc91rqll58fj4o9s0';

type Status = 'idle' | 'submitting' | 'success' | 'error';

interface ParsedResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const topics = ['内容建议', '资料纠错', '合作洽谈', '问题反馈', '其他'];

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    const email = String(formData.get('email') || '').trim();
    const message = String(formData.get('message') || '').trim();
    if (!email || !message) {
      setStatus('error');
      setFormError('请填写邮箱与留言内容后再提交。');
      return;
    }

    setStatus('submitting');
    setFormError('');

    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key === 'website_alt') return;
        payload.append(key, String(value));
      });

      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: ParsedResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ParsedResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK' && !isSpam;

      if (isOk) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg.trim()
            ? serverMsg
            : '提交失败，请稍后重试。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请检查网络后重试。');
    }
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400';

  return (
    <form
      id="contact-form-about"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6"
      noValidate
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950">给我们留言</h3>
      <p className="mt-1 text-sm text-foreground-600">
        有任何建议或合作意向，欢迎通过下方表单联系我们。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block font-label text-sm text-foreground-700">
            您的称呼
          </label>
          <input id="contact-name" name="name" type="text" placeholder="请输入称呼" className={inputClass} />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block font-label text-sm text-foreground-700">
            邮箱 <span className="text-accent-600">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-subject" className="mb-1.5 block font-label text-sm text-foreground-700">
          咨询主题
        </label>
        <select id="contact-subject" name="subject" defaultValue={topics[0]} className={`${inputClass} cursor-pointer`}>
          {topics.map((topic) => (
            <option key={topic} value={topic}>
              {topic}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className="mb-1.5 block font-label text-sm text-foreground-700">
          留言内容 <span className="text-accent-600">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={5}
          maxLength={500}
          placeholder="请简要描述您的建议或问题（最多 500 字）"
          className={`${inputClass} resize-none`}
        />
      </div>

      <input
        type="text"
        name="website_alt"
        className="contact-alt-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === 'error' && formError && (
        <p className="mt-4 flex items-start gap-2 rounded-md bg-accent-50 px-3 py-2.5 text-sm text-accent-900">
          <i className="ri-error-warning-line mt-0.5" aria-hidden="true" />
          <span>{formError}</span>
        </p>
      )}

      {status === 'success' && (
        <p className="mt-4 flex items-start gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900">
          <i className="ri-checkbox-circle-line mt-0.5" aria-hidden="true" />
          <span>留言已提交，感谢您的反馈，我们会尽快回复。</span>
        </p>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 font-label text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
        >
          {status === 'submitting' ? (
            <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
          ) : (
            <i className="ri-send-plane-line" aria-hidden="true" />
          )}
          {status === 'submitting' ? '提交中…' : '提交留言'}
        </button>
        <span className="text-xs text-foreground-500">我们通常会在 1-2 个工作日内回复。</span>
      </div>
    </form>
  );
}